import React, { useState, useEffect, useMemo } from "react";
import { Box, Button, IconButton, Tooltip, Typography } from "@mui/material/";
import { useSelector, useDispatch } from "react-redux";
import { groupBy } from "lodash";
import { strings } from "../../../../common/i18n";
import { enums, helpers } from "../../../../common/lib";
import { v4 as uuidv4 } from "uuid";
import { FaFirefox, FaEdge, FaGlobe } from "react-icons/fa";
import { FiChrome } from "react-icons/fi";
import { ImSafari } from "react-icons/im";
import ReplayIcon from "@mui/icons-material/Replay";
import SyncIcon from "@mui/icons-material/Sync";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import actions from "../../../../Store/actions";
import { logToSentry } from "../../../../config/Sentry";
import AssetProgresBar from "../../../../common/components/AssetProgresBar";
import AssetStatus from "../../../../common/components/AssetStatus";
import { format } from "../../../../common/lib";
import styles from "./styles";
import { useReissuedAsset } from "../../../../common/hooks";

/**
 *  Hook for user table data and operations
 */

// Browser icon mapping
const browserIcons = {
    Chrome: <FiChrome title="Chrome" size={14} />,
    Firefox: <FaFirefox title="Firefox" size={14} />,
    Safari: <ImSafari title="Safari" size={14} />,
    "Mobile Safari": <ImSafari title="Mobile Safari" size={14} />,
    Edge: <FaEdge title="Edge" size={14} />,
    default: <FaGlobe title="Default" size={14} />
};

function formatDownloadInfo(downloadStr) {
    if (!downloadStr || typeof downloadStr !== "string") return [];

    const rawDownloads = downloadStr
        .split("}},")
        .map((s) => s.trim())
        .filter(Boolean);

    const formatted = rawDownloads.map((entry) => {
        let text = entry;

        // Remove trailing '}}'
        text = text.endsWith("}}") ? text.slice(0, -2) : text;

        // Extract ISO date at the start (optional)
        const dateMatch = text.match(
            /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z/
        );
        let datePart = "";
        if (dateMatch) {
            const isoDate = dateMatch[0];
            datePart = format.displayDatesWithTime(isoDate); // your formatter
            text = text.replace(isoDate, "").trim();
        }

        // Remove navigator / JSON
        const lastDoubleColonIndex = text.lastIndexOf("::");
        if (lastDoubleColonIndex !== -1) {
            text = text.slice(0, lastDoubleColonIndex).trim();
        }

        // Normalize separators
        text = text
            .replace(/^::/, "")
            .replace(/::/g, " - ")
            .replace(/\s*:\s*/g, ":")
            .replace(/\s+/g, " ")
            .trim();

        // Combine date and download info
        return datePart ? `${datePart} - ${text}` : text;
    });

    // Sort descending by date
    return formatted.sort((a, b) => {
        const aDate = Date.parse(a.split(" - ")[0]);
        const bDate = Date.parse(b.split(" - ")[0]);
        if (isNaN(aDate) || isNaN(bDate)) return 0;
        return aDate - bDate;
    });
}
export default () => {
    const {
        accounts,
        assetAuditors,
        assignmentDetails,
        altAssets,
        auditReview,
        auditAssignments,
        auditLocations,
        locations,
        userOrganisations,
        typesList
    } = useSelector((state) => state);

    const dispatch = useDispatch();
    const [loading, setLoading] = useState(true);
    const [showAuditorList, setShowAuditorList] = useState(false);
    const [recalling, setRecalling] = useState(false);

    const canReissueAssets = useReissuedAsset();

    useEffect(() => setShowAuditorList(canReissueAssets), [canReissueAssets]);

    const orgId = userOrganisations.item.id;

    /** Can recall/reassign the assignemnt */
    const canRecall = (statusId) =>
        statusId === enums.status.auditAssignment.pendingDownload;

    /** Can send the assignemnt  */
    const canSendReminder = (statusId) =>
        statusId === enums.status.auditAssignment.pendingSubmission ||
        statusId === enums.status.auditAssignment.pendingDownload;

    const handleRecall = (row) => async () => {
        setRecalling(true);
        try {
            const res = assignmentDetails.data.find(
                (item) => item.id === row.auditAssignmentId
            );

            const next = {
                id: row.auditAssignmentId,
                status: enums.status.auditAssignment.recalled
            };
            await dispatch(
                actions.auditAssignments.create.bulkUpdatePostRequest([next])
            );
            await dispatch(
                actions.assignmentDetails.create.merge({ ...res, ...next })
            );
        } catch (error) {
            const errStr = error.toString();
            console.error(errStr);
            logToSentry("On recalling:", { error });
            dispatch(actions.toast.create.error(strings("errors.onRecall")));
            console.error(error);
        }
        setRecalling(false);
    };

    const handleReminder = (auditAssignmentId: number) => async () => {
        try {
            await dispatch(
                actions.auditResendAssignmentEmail.create.getRequest(
                    `assignment_id=${auditAssignmentId}`
                )
            );
            dispatch(
                actions.toast.create.success(strings("messages.reminderEmail"))
            );
        } catch (error) {
            const errStr = error.toString();
            console.error(errStr);
            logToSentry("On reminder email:", { error });
            dispatch(
                actions.toast.create.error(strings("errors.onReminderEmail"))
            );
            console.error(error);
        }
    };
    const handleRestAuditAssignment = (auditAssignmentId) => async () => {
        try {
            await dispatch(
                actions.auditAssignments.create.patchRequest(
                    auditAssignmentId,
                    orgId,
                    {
                        status: 1
                    }
                )
            );
            dispatch(
                actions.toast.create.success(
                    strings("messages.resetAuditAssignment")
                )
            );
        } catch (error) {
            const errStr = error.toString();
            console.error(errStr);
            logToSentry("On reset audit assignment:", { error });
            dispatch(
                actions.toast.create.error(strings("errors.onresetAssignment"))
            );
            console.error(error);
        }
    };

    /** Get audit status  */
    const getAuditStatus = (statusId) => {
        const { auditStatus } = typesList;
        return helpers.getListItemForId(auditStatus, statusId);
    };

    /** Remove Duplicates */
    const removeDuplicates = (array) => {
        const uniqueIds = {};

        return array.reduce((result, current) => {
            const id = current.auditAsset;
            if (
                !uniqueIds[id] ||
                new Date(current.assignedDate) >
                    new Date(uniqueIds[id].assignedDate)
            ) {
                uniqueIds[id] = current;
            }
            return Object.values(uniqueIds);
        }, []);
    };

    const data = useMemo(() => {
        setLoading(true);
        const devicePattern = /([A-Za-z ]+ : .*? -- {.*?})/gs;

        const assetAuditorsData = removeDuplicates(assetAuditors.data);

        const byAuditor = groupBy(assetAuditorsData, (item) => item.auditor);
        const auditorAssigned = Object.entries(byAuditor).map(
            ([key, value]) => {
                const account = helpers.getListItemForId(accounts, key);
                const res = {
                    seen: 0,
                    altAssets: 0,
                    excluded: 0
                };
                res.altAssets = altAssets.data.filter(
                    (item) => account && account.id === item.createdBy
                ).length;

                value.map((item) => {
                    if (item.exclusionReason) res.excluded += 1;
                    if (!item.exclusionReason) res.seen += 1;
                });

                return {
                    id: key,
                    email: account ? account.email : "",
                    ...res,
                    count: value.length + res.altAssets
                };
            }
        );
        const { auditAssignmentStatus } = typesList;
        const result = assignmentDetails.data.map((item, idx) => {
            const id = uuidv4();

            const auditLocation = helpers.getListItemForId(
                auditLocations,
                item.auditLocation
            );
            const auditor = helpers.getListItemForId(accounts, item.auditor);
            const assignmentDetail = assignmentDetails.data.find(
                (detail) => detail.auditor === item.auditor
            );

            const location = helpers.getListItemForId(
                locations,
                auditLocation?.location
            );
            const status = helpers.getListItemForId(
                auditAssignmentStatus,
                item.status
            );

            // let downloads = [];
            // if (item.downloadInformation) {
            //     console.log("downloadInformation", item.downloadInformation);
            //     // Match all devices first
            //     const matches =
            //         item.downloadInformation.matchAll(devicePattern);
            //     const deviceBlocks = matches
            //         ? Array.from(matches, (m) => m[0])
            //         : [];

            //     // For each device, take only the part before "::"
            //     downloads = deviceBlocks.map((block) =>
            //         block.split("::").shift()?.trim()
            //     );
            // }

            return {
                id,
                dataIndex: idx,
                auditAssignmentId: item.id,
                auditorAssigned,
                isReissued: item.isReissued,
                auditor: auditor
                    ? `${auditor.displayName}${auditor.email}`
                    : "",
                displayName: auditor ? auditor.displayName : "",
                assetSummary: {
                    altAsset: {
                        pending: 0,
                        resolved: 0,
                        total: 0
                    },
                    auditAsset: {
                        approved: 0,
                        rejected: 0,
                        pending:
                            assignmentDetail &&
                            assignmentDetail.summary.unchecked
                                ? assignmentDetail.summary.unchecked
                                : 0,
                        unchecked:
                            assignmentDetail && assignmentDetail.summary.pending
                                ? assignmentDetail.summary.pending
                                : 0,
                        excluded:
                            assignmentDetail &&
                            assignmentDetail.summary.excluded
                                ? assignmentDetail.summary.excluded
                                : 0,
                        seen:
                            assignmentDetail && assignmentDetail.summary.checked
                                ? assignmentDetail.summary.checked
                                : 0,
                        total:
                            assignmentDetail && assignmentDetail.summary.total
                    },
                    status: getAuditStatus(item.status)
                },
                auditorComment:
                    assignmentDetail && assignmentDetail.auditorComment
                        ? assignmentDetail.auditorComment
                        : "",
                auditorSignature:
                    assignmentDetail && assignmentDetail.auditorSignature
                        ? assignmentDetail.auditorSignature
                        : "",
                downloadInformation: item.downloadInformation,
                email: auditor ? auditor.email : "",
                location: location?.name ?? "",
                auditorStatusString: status?.name,
                auditorStatus: status,
                statusId: status?.id,
                status: getAuditStatus(item.status)
            };
        });
        setLoading(false);
        return result;
    }, [
        accounts,
        auditAssignments,
        auditReview,
        assignmentDetails.data,
        auditLocations,
        locations,
        typesList,
        altAssets
    ]);

    /** On columns  */
    const columns = useMemo(() => {
        const statusFilter = typesList.auditAssignmentStatus.data.map(
            (item) => item.name
        );

        return [
            {
                accessorKey: "auditor",
                header: strings("auditor"),
                enableHiding: false,
                enableColumnFilter: false,
                enableGlobalFilter: true,
                Cell: ({ row }) => {
                    const { displayName, email } = row.original;
                    return (
                        <Box>
                            <Typography variant="caption" component="div">
                                {displayName}
                            </Typography>
                            <Typography variant="caption" component="div">
                                {email}
                            </Typography>
                        </Box>
                    );
                }
            },
            {
                accessorKey: "downloadInformation",
                header: strings("downloadedOn"),
                enableGlobalFilter: true,
                filterFn: (row, _columnId, filterValue) => {
                    const downloadStr = row.original.downloadInformation;
                    if (!downloadStr) return false;
                    const text = formatDownloadInfo(downloadStr).join(" ");
                    return text
                        .toLowerCase()
                        .includes(filterValue.toLowerCase());
                },
                Cell: ({ cell }) => {
                    const downloadStr = cell.getValue();
                    if (!downloadStr) return null;

                    const downloads = formatDownloadInfo(downloadStr);
                    const firstTwo = downloads.slice(0, 2);
                    const remaining = downloads.slice(2);

                    const renderDownload = (item, idx) => {
                        const parts = item.split(":");
                        const browserName =
                            parts[parts.length - 2]?.trim() || "";
                        const icon =
                            browserIcons[browserName] || browserIcons.default;

                        return (
                            <Box
                                key={idx}
                                display="flex"
                                alignItems="center"
                                mb={0.5}
                            >
                                {icon}
                                <Typography variant="caption" ml={0.5}>
                                    {item}
                                </Typography>
                            </Box>
                        );
                    };

                    return (
                        <Box>
                            {firstTwo.map(renderDownload)}
                            {remaining.length > 0 && (
                                <Tooltip
                                    title={
                                        <Box>
                                            {remaining.map((item, idx) => (
                                                <div key={idx}>{item}</div>
                                            ))}
                                        </Box>
                                    }
                                >
                                    <Box
                                        sx={{
                                            color: "#888",
                                            cursor: "pointer",
                                            mt: 0.5
                                        }}
                                    >
                                        +{remaining.length} more
                                    </Box>
                                </Tooltip>
                            )}
                        </Box>
                    );
                }
            },
            {
                id: "assets",
                accessorFn: () => "",
                header: strings("assets"),
                enableColumnFilter: false,
                enableGlobalFilter: false,
                Cell: ({ row }) => <AssetProgresBar data={row.original} />
            },
            {
                accessorKey: "auditorStatusString",
                header: strings("status"),
                filterVariant: "multi-select",
                filterSelectOptions: statusFilter,
                Cell: ({ row }) => (
                    <AssetStatus status={row.original.auditorStatus} />
                )
            },
            {
                id: "reissued",
                accessorKey: "isReissued",
                header: strings("reissued"),
                enableSorting: false,
                enableColumnFilter: false,
                enableGlobalFilter: false,
                Cell: ({ row }) =>
                    row.original.isReissued ? (
                        <Tooltip title={strings("reissued")}>
                            <SyncIcon color="primary" />
                        </Tooltip>
                    ) : (
                        <>-</>
                    )
            }
        ];
    }, [auditAssignments, assignmentDetails, recalling, typesList]);

    const rowActions = (row) => (
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "nowrap" }}>
            {canSendReminder(row.original.statusId) ? (
                <IconButton
                    size="small"
                    onClick={handleReminder(row.original.auditAssignmentId)}
                >
                    <Tooltip title={strings("reminder")}>
                        <NotificationsNoneIcon fontSize="small" color="primary" />
                    </Tooltip>
                </IconButton>
            ) : (
                <Box sx={{ width: 30 }} />
            )}

            {canRecall(row.original.statusId) ? (
                <IconButton
                    size="small"
                    disabled={recalling}
                    onClick={handleRecall(row.original)}
                >
                    <Tooltip title={strings("recall")}>
                        <DeleteOutlineIcon fontSize="small" color="primary" />
                    </Tooltip>
                </IconButton>
            ) : (
                <Box sx={{ width: 30 }} />
            )}

            {canSendReminder(row.original.statusId) ? (
                <IconButton
                    size="small"
                    onClick={handleRestAuditAssignment(
                        row.original.auditAssignmentId
                    )}
                >
                    <Tooltip title={strings("resetAuditAssignment")}>
                        <ReplayIcon fontSize="small" color="primary" />
                    </Tooltip>
                </IconButton>
            ) : (
                <Box sx={{ width: 30 }} />
            )}
        </Box>
    );

    /** Toolbar Component */
    const onAddAuditors = () => setShowAuditorList(true);

    /** on Back  */
    const onBack = () => setShowAuditorList(false);

    const toolBarComponents = () => (
        <Button
            sx={styles.primaryButton}
            variant="contained"
            size="small"
            color="primary"
            onClick={onAddAuditors}
            endIcon={<AddIcon />}
        >
            {strings("addAuditors")}
        </Button>
    );

    return {
        showAuditorList,
        loading,
        columns,
        data,
        rowActions,
        onBack,
        toolBarComponents
    };
};
